/**
 * A click opens a block; a hold peeks at it (ADR 0193, decisions 1–5 and 9,
 * as ADR 0194 decisions 4 and 7 amend them), as the owner's prototype does,
 * for a block's card and a ticket's.
 *
 * - **A click opens its popup,** as it always has: the button's own click.
 * - **A press shows at once:** the card dips slightly, with the brand
 *   outline, before the hold counts.
 * - **Held still for the tech's hold time** (120 ms; `useUserSettings`), it
 *   peeks: it opens up in place while it's held, over 200 ms, and folds over
 *   160 ms when it's let go, easing in and out so its edge is seen to travel.
 *   The cards under it move with it. The popup doesn't open.
 * - **It's seen whole:** near the window's foot, or a scroll box's (the
 *   tray's), the page follows it as it opens (the owner, 2026-10-27: "i cant
 *   see the bottom"), its head kept in view if it's taller than the window.
 *   Folding moves nothing.
 * - **Let go within 300 ms, it was a click,** even if it had started to
 *   peek: it snaps back, with no fold to watch, and the popup opens.
 * - **Moved more than 6 px, it's a drag** (ADR 0118's merge, or a drop on an
 *   open ticket, ADR 0187), by the browser's own drag and drop: a peek folds
 *   as it starts, and a card that can't be dragged only folds.
 * - **Enter opens the popup. Space does what the mouse button does:** it
 *   dips at once; a tap opens it, as a button's Space always has; held, it
 *   peeks until it's let go. Space is for the card under the mouse or the
 *   focused one, whichever was used last (`boardKeys`).
 *
 * Only with a mouse, or Space, and never on a phone (under 640 px, the
 * popup's own breakpoint; ADR 0184): a tap there opens the popup or its
 * sheet, as before, and so does touch on a wider screen.
 *
 * **A right-click ends a press or a peek under way** (ADR 0194, decision 3):
 * the card's menu opens instead, and the left button's click, when it's let
 * go, opens nothing. A press with Ctrl, ⌘ or Shift is never a hold: those
 * clicks select cards (decision 5).
 *
 * The peek is the card's own: not a selection, so the ribbon and the board's
 * other cards are as they were, but for those under it moving down as it
 * grows.
 */
import {
  type KeyboardEvent,
  type PointerEvent,
  type Ref,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { flushSync } from "react-dom";

import { registerCard } from "./boardKeys";

/**
 * A press let go sooner than this is a click, peek or not (ADR 0193, decision
 * 3): 300 ms since #841, where 180 ms missed a deliberate, slower click.
 */
export const CLICK_MS = 300;
/** A press that moves further than this is a drag (decision 5). */
export const DRAG_PX = 6;
/**
 * How long a peek takes to open, and to fold (ADR 0194, decision 7), easing
 * in and out: the curve before it moved most of the way in its first frame,
 * so the card seemed to jump.
 */
export const PEEK_OPEN_MS = 200;
export const PEEK_FOLD_MS = 160;
export const PEEK_EASE = "cubic-bezier(.3, 0, .2, 1)";
/** A phone: no peek (the owner: "All these changes are for desktop"). */
export const PHONE_QUERY = "(max-width: 639px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/**
 * A pressed card's dip (decision 4; the prototype's `.pressing`), beside its
 * brand outline, which is its border: none with reduced motion.
 */
export const PRESS_DIP = "motion-safe:scale-[0.985]";
/** What the dip and the outline ease with, each way, as the prototype's do. */
export const PRESS_EASE = "motion-safe:transition-[scale,border-color] motion-safe:duration-[80ms] motion-safe:ease-out";
/**
 * A peeking card (ADR 0194, decision 7; the prototype's `look-outline`): its
 * own background and text, with the brand border and outline, as a pressed
 * card has. Not filled Axon blue, which flashed as it opened.
 */
export const PEEK_OUTLINE =
  "border-brand shadow-[0_0_0_1px_var(--brand),0_12px_28px_rgba(0,0,0,0.06)] " +
  "dark:shadow-[0_0_0_1px_var(--brand),0_12px_28px_rgba(0,0,0,0.4)]";

/**
 * The room a peek keeps from the window's edge when the page follows it, as
 * the board's keys keep for a card they move to (12 px): its outline and
 * shadow show too.
 */
export const PEEK_ROOM = "scroll-my-3";

/**
 * The page follows a peek, by the least that shows it whole, with its room
 * (`PEEK_ROOM`), its head first: nothing moves while it's in view. At once,
 * not smoothly: it's called each frame while the peek grows.
 */
function keepInView(card: HTMLElement) {
  if (typeof card.scrollIntoView === "function") {
    card.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "instant" });
  }
}

function matches(query: string): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(query).matches;
}

function subscribePhone(onChange: () => void): () => void {
  if (typeof window.matchMedia !== "function") {
    return () => undefined;
  }
  const list = window.matchMedia(PHONE_QUERY);
  list.addEventListener("change", onChange);
  return () => {
    list.removeEventListener("change", onChange);
  };
}

/** Whether the window is a phone's width, kept current. */
export function usePhone(): boolean {
  return useSyncExternalStore(subscribePhone, () => matches(PHONE_QUERY));
}

interface Press {
  /** A mouse's pointer, or null for Space. */
  pointerId: number | null;
  x: number;
  y: number;
  /** Past the click cut-off: letting go of a peek now only folds it. */
  late: boolean;
  /** It moved too far to be a hold: it's a drag, or nothing. */
  moved: boolean;
  /** It peeked, at some point. */
  peeked: boolean;
  timers: ReturnType<typeof setTimeout>[];
  /** Takes the window's listeners away. */
  stop: () => void;
}

export interface Peek {
  /** Held, before it peeks: the card dips, with the brand outline. */
  pressing: boolean;
  /** It's peeking: its peek shows. */
  peeking: boolean;
  /** It can peek here: not on a phone. */
  available: boolean;
  /** The card's button's own handlers, and its ref. */
  button: {
    ref: (button: HTMLButtonElement | null) => void;
    onPointerDown: (event: PointerEvent<HTMLElement>) => void;
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
    onClick: () => void;
    onBlur: () => void;
  };
  /** A drag began from the card: a peek folds first, so the drag shows the card as it is. */
  onDragStart: () => void;
  /** Its menu opened (☰, Shift+F10): a press or a peek under way ends, and opens nothing. */
  cancel: () => void;
}

/** Sets a ref the card was given, beside the peek's own. */
function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref !== null && ref !== undefined) {
    ref.current = value;
  }
}

/**
 * The hold-to-peek of a card whose click calls `onOpen`. `holdMs`: how long
 * it's held still before it peeks, the tech's own once that's a setting.
 * `ref`: the card's button, for its owner too.
 */
export function usePeek({
  onOpen,
  holdMs,
  ref,
}: {
  onOpen: () => void;
  holdMs: number;
  ref?: Ref<HTMLButtonElement> | undefined;
}): Peek {
  const phone = usePhone();
  const [pressing, setPressing] = useState(false);
  const [peeking, setPeeking] = useState(false);
  const press = useRef<Press | null>(null);
  // What's showing now, for the window's listeners, which outlive a render.
  const shown = useRef(false);
  // The card's button: what grows, and shrinks back.
  const element = useRef<HTMLElement | null>(null);
  // Its height before the peek opened or folded, to grow or shrink from.
  const from = useRef<number | null>(null);
  const motion = useRef<Animation | null>(null);
  // The next frame that follows a peek's foot as it opens.
  const following = useRef<number | null>(null);
  // The click that ends a peek opens nothing.
  const swallow = useRef(false);

  const stopFollowing = () => {
    if (following.current !== null) {
      cancelAnimationFrame(following.current);
      following.current = null;
    }
  };

  /** Opens or folds the peek; `animate`: it grows or shrinks to its new height. */
  const show = (next: boolean, animate: boolean) => {
    if (shown.current === next) {
      return;
    }
    shown.current = next;
    stopFollowing();
    const card = element.current;
    // The height it's drawn at now, partway through a motion or not. Its
    // layout's: a pressed card's dip (a scale) doesn't change it, so the dip
    // is never taken for the card changing size.
    from.current =
      animate && card !== null && typeof card.animate === "function" && !matches(REDUCED_MOTION_QUERY)
        ? card.offsetHeight
        : null;
    motion.current?.cancel();
    motion.current = null;
    setPeeking(next);
  };

  // After it's drawn at its new height: from the old one to it, as the
  // prototype's cards do. The cards under it follow, as it grows. Only its
  // height moves: its contents show as it grows, with no fade or slide,
  // which would draw them as a layer and redraw them sharp a moment later.
  //
  // Opening, the page follows its foot each frame while it grows, so the
  // window never has to catch up with it at the end.
  useLayoutEffect(() => {
    const height = from.current;
    from.current = null;
    const card = element.current;
    if (card === null) {
      return;
    }
    if (height !== null) {
      const to = card.offsetHeight;
      if (Math.abs(to - height) >= 1) {
        motion.current = card.animate(
          [
            { height: `${String(height)}px`, overflow: "hidden" },
            { height: `${String(to)}px`, overflow: "hidden" },
          ],
          { duration: to > height ? PEEK_OPEN_MS : PEEK_FOLD_MS, easing: PEEK_EASE },
        );
      }
    }
    if (!peeking) {
      return;
    }
    const opening = motion.current;
    const follow = () => {
      following.current = null;
      keepInView(card);
      if (opening?.playState === "running" && typeof requestAnimationFrame === "function") {
        following.current = requestAnimationFrame(follow);
      }
    };
    follow();
  }, [peeking]);

  /** The click that a peek's release brings opens nothing; it comes at once, or not at all. */
  const swallowClick = () => {
    swallow.current = true;
    setTimeout(() => {
      swallow.current = false;
    }, 0);
  };

  /** The press is over: its timers and listeners go. */
  const end = (): Press | null => {
    const held = press.current;
    if (held === null) {
      return null;
    }
    press.current = null;
    for (const timer of held.timers) {
      clearTimeout(timer);
    }
    held.stop();
    setPressing(false);
    return held;
  };

  /** Pressed: it dips at once, and peeks if it's held still long enough. */
  const begin = (card: HTMLElement, pointerId: number | null, x: number, y: number, stop: () => void) => {
    end();
    swallow.current = false;
    element.current = card;
    const held: Press = { pointerId, x, y, late: false, moved: false, peeked: false, timers: [], stop };
    held.timers.push(
      setTimeout(() => {
        held.peeked = true;
        show(true, true);
      }, holdMs),
      setTimeout(() => {
        held.late = true;
      }, CLICK_MS),
    );
    press.current = held;
    setPressing(true);
  };

  /**
   * Let go. Before it peeked, that's a click, as ever. After, within the
   * click cut-off it's still one: the card snaps back and the click opens the
   * popup. Later, the card folds and the click opens nothing.
   */
  const release = (): "click" | "fold" => {
    const held = end();
    if (held === null || !shown.current) {
      return held?.peeked === true ? "fold" : "click";
    }
    if (!held.late) {
      show(false, false);
      return "click";
    }
    show(false, true);
    return "fold";
  };

  /** It moved too far: no peek now. A card that can be dragged is dragged. */
  const moved = () => {
    const held = press.current;
    if (held === null || held.moved) {
      return;
    }
    held.moved = true;
    for (const timer of held.timers) {
      clearTimeout(timer);
    }
    held.timers = [];
    setPressing(false);
    show(false, true);
  };

  /** Called off: the drag took the pointer, the window lost focus. */
  const cancel = (animate: boolean) => {
    end();
    show(false, animate);
  };

  // Nothing left running once the card's gone.
  useEffect(
    () => () => {
      const held = press.current;
      press.current = null;
      if (held !== null) {
        for (const timer of held.timers) {
          clearTimeout(timer);
        }
        held.stop();
      }
      motion.current?.cancel();
      stopFollowing();
    },
    [],
  );

  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    // A new press: whatever ended the last one (a right-click) is over.
    swallow.current = false;
    // A mouse's main button, with nothing that makes it another kind of
    // click (Ctrl on a Mac is its right click; Ctrl, ⌘ and Shift select,
    // ADR 0194 decision 5). Touch and pens tap, as ever.
    if (
      phone ||
      event.pointerType !== "mouse" ||
      event.button !== 0 ||
      !event.isPrimary ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey
    ) {
      return;
    }
    const id = event.pointerId;
    const onMove = (e: globalThis.PointerEvent) => {
      const held = press.current;
      if (held !== null && e.pointerId === id && Math.hypot(e.clientX - held.x, e.clientY - held.y) > DRAG_PX) {
        moved();
      }
    };
    const onUp = (e: globalThis.PointerEvent) => {
      if (e.pointerId !== id) {
        return;
      }
      const held = press.current;
      if (held?.moved === true) {
        end();
        if (held.peeked) {
          swallowClick();
        }
      } else if (release() === "fold") {
        swallowClick();
      }
    };
    const onCancel = () => {
      cancel(true);
    };
    // A right-click while it's held (ADR 0194, decision 3): the menu opens,
    // and the left button's click, when it's let go, opens nothing.
    const onMenu = () => {
      cancel(true);
      swallow.current = true;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("blur", onCancel);
    window.addEventListener("contextmenu", onMenu);
    begin(event.currentTarget, id, event.clientX, event.clientY, () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("blur", onCancel);
      window.removeEventListener("contextmenu", onMenu);
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter") {
      // The button's own click follows, and opens the popup.
      swallow.current = false;
    }
  };

  /**
   * Space pressed for this card (`boardKeys`), focused or under the mouse: it
   * dips at once, as a mouse's press does, and peeks if it's held.
   */
  const spaceDown = () => {
    const card = element.current;
    if (card === null || press.current !== null) {
      return;
    }
    // The window losing focus takes the key's release with it; a
    // right-click ends it too, its menu opening instead (ADR 0194).
    const onCancel = () => {
      cancel(true);
    };
    window.addEventListener("blur", onCancel);
    window.addEventListener("contextmenu", onCancel);
    begin(card, null, 0, 0, () => {
      window.removeEventListener("blur", onCancel);
      window.removeEventListener("contextmenu", onCancel);
    });
  };

  /** Space let go: a tap opens the popup, as a click does; a peek folds. */
  const spaceUp = () => {
    // Its press was called off already: focus moved on, or the window went.
    if (press.current?.pointerId !== null) {
      return;
    }
    if (release() === "click") {
      // As a click would: the card has focus, to come back to from the popup.
      const card = element.current;
      if (card !== null && document.activeElement !== card) {
        card.focus({ preventScroll: true });
      }
      onOpen();
      // A browser that clicks a button as Space is let go anyway opens it once.
      swallowClick();
    }
  };

  // What Space does, kept current for the board, which outlives a render.
  const space = useRef({ down: spaceDown, up: spaceUp });
  useLayoutEffect(() => {
    space.current = { down: spaceDown, up: spaceUp };
  });
  // On the board's keys while it's on the page.
  useEffect(() => {
    const card = element.current;
    if (card === null) {
      return;
    }
    return registerCard(card, {
      available: () => !matches(PHONE_QUERY),
      down: () => {
        space.current.down();
      },
      up: () => {
        space.current.up();
      },
    });
  }, []);

  return {
    pressing: pressing && !peeking,
    peeking,
    available: !phone,
    button: {
      ref: (button) => {
        element.current = button;
        assignRef(ref, button);
      },
      onPointerDown,
      onKeyDown,
      onClick: () => {
        if (swallow.current) {
          swallow.current = false;
          return;
        }
        onOpen();
      },
      onBlur: () => {
        // Space held as focus moves on, or the mouse's press with the window: it folds.
        if (press.current !== null) {
          cancel(true);
        }
      },
    },
    onDragStart: () => {
      // Folded before the browser draws what's dragged: the card as it is.
      flushSync(() => {
        cancel(false);
      });
      // A drag never opens the popup, whatever a browser does as it ends.
      // The card's next press, or Enter, starts afresh.
      swallow.current = true;
    },
    cancel: () => {
      if (press.current !== null || shown.current) {
        cancel(true);
        // A mouse's press: its click, when it's let go, opens nothing.
        swallow.current = true;
      }
    },
  };
}
